import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  hideNotification,
  loadNotification,
  loadNotificationFailure,
  loadNotifications,
  loadNotificationsFailure,
  loadNotificationsSuccess,
  loadNotificationSuccess,
  markNotificationAsRead,
  markNotificationAsUnRead,
  openNotificationDetails,
  updateNotification,
  updateNotificationFailure,
  updateNotificationSuccess
} from './notification.actions';
import {NotificationApiService} from '../services/notification-api.service';
import {catchError, filter, map, of, switchMap, tap} from 'rxjs';
import {Store} from '@ngrx/store';
import {GlobalState} from '../../../store/store.model';
import {
  NotificationDetailsBottomSheetComponent
} from '../components/bottom-sheets/notification-details-bottom-sheet/notification-details.bottom-sheet';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {TranslocoService} from '@jsverse/transloco';
import {MatSnackBar} from '@angular/material/snack-bar';

@Injectable()
export class NotificationEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(NotificationApiService);
  private readonly _store = inject(Store<GlobalState>);
  private readonly _bottomSheet = inject(MatBottomSheet)
  private _snackBar = inject(MatSnackBar);
  private _transloco = inject(TranslocoService);

  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadNotifications),
      switchMap(() =>
        this.api.getNotifications().pipe(
          map(({notifications}) => loadNotificationsSuccess({notifications})),
          catchError(error => of(loadNotificationsFailure({error})))
        )
      )
    )
  );

  loadNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadNotification),
      switchMap(({id}) =>
        this.api.getById(id).pipe(
          map(({notification}) => loadNotificationSuccess({notification})),
          catchError(error => of(loadNotificationFailure({error})))
        )
      )
    )
  );

  updateNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateNotification),
      switchMap(({id, read, hide}) =>
        this.api.update(id, {read, hide}).pipe(
          map(() => updateNotificationSuccess({id, read, hide})), // ← usa solo ciò che hai
          catchError(error => of(updateNotificationFailure({error})))
        )
      )
    )
  );


  aliasMarkNotificationAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(markNotificationAsRead),
      map(({id}) => updateNotification({id, read: true}))
    )
  );

  aliasMarkNotificationAsUnRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(markNotificationAsUnRead),
      map(({id}) => updateNotification({id, read: false}))
    )
  );

  aliasHideNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(hideNotification),
      map(({id}) => updateNotification({id, hide: true}))
    )
  );

  openNotificationDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openNotificationDetails),
        tap(({id, title, body}) => {
          // 1. Apri il bottom sheet
          this._bottomSheet.open(NotificationDetailsBottomSheetComponent, {
            data: {id, title, body},
            panelClass: 'custom-bottom-sheet'
          });

          // 2. Dispatch per marcare come letta
          this._store.dispatch(updateNotification({id, read: true}));
        })
      ),
    {dispatch: false}
  );

  updateNotificationSuccessFeedback$ = createEffect(() =>
      this.actions$.pipe(
        ofType(updateNotificationSuccess),
        filter(({hide}) => hide === true), // mostra feedback solo se hide è true
        tap(() => {
          this._snackBar.open(
            this._transloco.translate('notification.hidden'),
            this._transloco.translate('common.close'),
            {duration: 3000, panelClass: 'snackbar'}
          );
        })
      ),
    {dispatch: false}
  );

}
